import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { BREAKDOWN_CATEGORIES } from "./constants";
import { ATTACHMENT_FIELDS } from "@/utils/afsol";

const fmtNum = (v) => {
  const digits = String(v).replace(/\D/g, "");
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};
const parseNum = (v) => String(v).replace(/\./g, "");

const WizardStepFinance = ({ form, setForm, updateField }) => {
  const [openCat, setOpenCat] = useState(null);
  const catRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (catRef.current && !catRef.current.contains(e.target)) setOpenCat(null); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addBreakdownItem = (category, subLabel) => {
    const item = { category, label: subLabel, amount: "", comment: "", fastanumer: "" };
    const bd = [...(form.payment.breakdown || []), item];
    setForm((f) => ({ ...f, payment: { ...f.payment, breakdown: bd } }));
    setOpenCat(null);
  };

  const removeBreakdownItem = (idx) => {
    const bd = form.payment.breakdown.filter((_, i) => i !== idx);
    setForm((f) => ({ ...f, payment: { ...f.payment, breakdown: bd } }));
  };

  const updateBreakdownItem = (idx, field, value) => {
    const bd = [...form.payment.breakdown];
    bd[idx] = { ...bd[idx], [field]: value };
    setForm((f) => ({ ...f, payment: { ...f.payment, breakdown: bd } }));
  };

  const breakdownTotal = (form.payment.breakdown || []).reduce(
    (sum, r) => sum + (parseInt(parseNum(r.amount)) || 0), 0
  );
  const purchaseNum = parseInt(parseNum(form.payment.purchasePrice)) || 0;
  const diff = breakdownTotal - purchaseNum;

  return (
    <div className="space-y-4" data-testid="wizard-step-finance">
      {/* Kaupverð */}
      <div>
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Greiðslufyrirkomulag</h3>
        <div>
          <Label className="text-[10px] text-slate-500">Umsamið kaupverð</Label>
          <div className="flex items-center gap-1">
            <Input
              value={fmtNum(form.payment.purchasePrice || "")}
              onChange={(e) => updateField("payment", "purchasePrice", parseNum(e.target.value))}
              placeholder="0"
              className="h-8 text-sm mt-0.5 w-40"
              data-testid="wizard-purchase-price"
            />
            <span className="text-[10px] text-slate-400 mt-0.5">kr.</span>
          </div>
        </div>
      </div>

      {/* Sundurliðun á kaupverði */}
      <div>
        <Label className="text-[10px] text-slate-500 mb-1 block">Sundurliðun á kaupverði</Label>
        <div className="flex items-center gap-3 mb-3 relative" ref={catRef}>
          {BREAKDOWN_CATEGORIES.map((cat) => (
            <div key={cat.key} className="relative">
              <button
                type="button"
                className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                onClick={() => {
                  if (cat.options === null) {
                    addBreakdownItem(cat.key, cat.label);
                  } else {
                    setOpenCat(openCat === cat.key ? null : cat.key);
                  }
                }}
                data-testid={`wizard-cat-${cat.key}`}
              >
                <Plus className="h-3 w-3" />
                {cat.label}
              </button>
              {openCat === cat.key && cat.options && (
                <div className="absolute top-6 left-0 z-50 bg-white border rounded-md shadow-lg py-1 min-w-[160px] max-h-[220px] overflow-y-auto">
                  {cat.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 transition-colors"
                      onClick={() => addBreakdownItem(cat.key, opt)}
                      data-testid={`wizard-cat-opt-${opt}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Breakdown items */}
        <div className="space-y-2">
          {(form.payment.breakdown || []).map((row, i) => (
            <div key={i} className="flex items-center gap-2 border-l-2 border-amber-500 pl-2" data-testid={`wizard-bd-row-${i}`}>
              <span className="text-xs font-medium text-slate-700 w-20 shrink-0 truncate" title={row.label}>{row.label}</span>
              <Input
                value={fmtNum(row.amount || "")}
                onChange={(e) => updateBreakdownItem(i, "amount", parseNum(e.target.value))}
                placeholder="0"
                className="h-7 text-xs w-24"
                data-testid={`wizard-bd-amount-${i}`}
              />
              <span className="text-[10px] text-slate-400">kr.</span>
              {row.category === "okutaeki" && (
                <Input
                  value={row.fastanumer || ""}
                  onChange={(e) => updateBreakdownItem(i, "fastanumer", e.target.value)}
                  placeholder="Fastanúmer"
                  className="h-7 text-xs w-20"
                  data-testid={`wizard-bd-fasta-${i}`}
                />
              )}
              <Input
                value={row.comment || ""}
                onChange={(e) => updateBreakdownItem(i, "comment", e.target.value)}
                placeholder="Athugasemdir"
                className="h-7 text-xs flex-1"
                data-testid={`wizard-bd-comment-${i}`}
              />
              <button type="button" onClick={() => removeBreakdownItem(i)} className="text-red-400 hover:text-red-600 shrink-0" data-testid={`wizard-bd-del-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Samtals */}
        {(form.payment.breakdown || []).length > 0 && (
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-200">
            <span className="text-xs font-semibold text-slate-700 w-20 shrink-0">Samtals</span>
            <div className="h-7 text-xs w-24 flex items-center font-semibold">{fmtNum(String(breakdownTotal))}</div>
            <span className="text-[10px] text-slate-400">kr.</span>
            {purchaseNum > 0 && diff !== 0 && (
              <span className={`text-[10px] px-2 py-0.5 rounded ${diff < 0 ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"}`}>
                {fmtNum(String(Math.abs(diff)))} kr. {diff < 0 ? "undir" : "yfir"} kaupverði
              </span>
            )}
          </div>
        )}
      </div>

      {/* Skattar */}
      <div>
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Skattar</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {[
            ["vehicleTaxPaid", "Bifreiðagjald greitt"],
            ["weightTaxPaid", "Þungaskattur greiddur"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={form.taxes[key]} onCheckedChange={(v) => updateField("taxes", key, v)} data-testid={`wizard-tax-${key}`} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Fylgiskjöl */}
      <div>
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Fylgiskjöl</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {ATTACHMENT_FIELDS.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={form.attachments[key]} onCheckedChange={(v) => updateField("attachments", key, v)} data-testid={`wizard-att-${key}`} />
              {label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WizardStepFinance;
